/** Largest sizes the server takes (docs/modules/pre-registration.md section 8): photo 400 KB, thumbnail 40 KB. */
const PHOTO_PX = 960;
const THUMB_PX = 160;
const PHOTO_MAX = 400 * 1024;
const THUMB_MAX = 40 * 1024;

function jpeg(canvas: HTMLCanvasElement, maxBytes: number): Promise<Blob | null> {
  const attempt = (quality: number): Promise<Blob | null> =>
    new Promise((resolve) => {
      canvas.toBlob(
        (b) => {
          if (b && b.size > maxBytes && quality > 0.3) resolve(attempt(quality - 0.15));
          else resolve(b && b.size <= maxBytes ? b : null);
        },
        'image/jpeg',
        quality,
      );
    });
  return attempt(0.85);
}

function scaled(img: ImageBitmap, longSide: number): HTMLCanvasElement {
  const k = Math.min(1, longSide / Math.max(img.width, img.height));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.width * k));
  c.height = Math.max(1, Math.round(img.height * k));
  c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

/**
 * Makes the 960 px photo and 160 px thumbnail in the browser (ADR 0017). Drawing to a canvas also drops the camera's
 * metadata (location, device). Returns null when the file is not a usable picture.
 */
export async function makePhotoPair(file: File): Promise<{ photo: Blob; thumb: Blob } | null> {
  try {
    const img = await createImageBitmap(file);
    const photo = await jpeg(scaled(img, PHOTO_PX), PHOTO_MAX);
    const thumb = await jpeg(scaled(img, THUMB_PX), THUMB_MAX);
    img.close();
    return photo && thumb ? { photo, thumb } : null;
  } catch {
    return null;
  }
}
